import { useEffect, useState, type FormEvent } from 'react';
import { api, newKey } from '../api/client';
import { useAuth } from '../auth/AuthContext';
import { useEmployee, useEmployees, useHrMutation, useLeaveBalance, useLeaveRequests, useLeaveTypes, useLoans, type Employee } from '../api/hr';
import { formatDate, formatGhs, parseCedis, type Tone } from '../format';
import { Badge, Button, Empty, ErrorNote, Field, Loading, Modal, useReport, useToast } from '../ui/kit';
import { PageHead } from './shared';

type Tab = 'staff' | 'leave' | 'advances';

export function People() {
  const { can } = useAuth();
  const [tab, setTab] = useState<Tab>('staff');
  return (
    <div className="page">
      <PageHead eyebrow="People" title="Staff and leave" />
      <div className="seg" role="group" aria-label="Show">
        <button type="button" aria-pressed={tab === 'staff'} onClick={() => setTab('staff')}>
          Staff
        </button>
        <button type="button" aria-pressed={tab === 'leave'} onClick={() => setTab('leave')}>
          Leave
        </button>
        {can('hr.pay_data') ? (
          <button type="button" aria-pressed={tab === 'advances'} onClick={() => setTab('advances')}>
            Advances
          </button>
        ) : null}
      </div>
      {tab === 'staff' ? <StaffTab /> : tab === 'leave' ? <LeaveTab /> : <AdvancesTab />}
    </div>
  );
}

/* ------------------------------------------------------------------- staff */
function StaffTab() {
  const { can } = useAuth();
  const [showEnded, setShowEnded] = useState(false);
  const [search, setSearch] = useState('');
  const list = useEmployees(showEnded ? undefined : 'ACTIVE');
  const [openId, setOpenId] = useState<string | null>(null);
  const [adding, setAdding] = useState(false);
  const q = search.trim().toLowerCase();
  const rows = (list.data ?? []).filter((e) => !q || `${e.fullName} ${e.employeeNo} ${e.position} ${e.department}`.toLowerCase().includes(q));

  return (
    <>
      <div className="row">
        {can('hr.manage') ? (
          <Button variant="primary" onClick={() => setAdding(true)}>
            Add a person
          </Button>
        ) : null}
        <input className="input" aria-label="Search staff" placeholder="Search name, number or job" value={search} onChange={(e) => setSearch(e.target.value)} style={{ maxWidth: 280 }} />
        <label className="row small">
          <input type="checkbox" checked={showEnded} onChange={(e) => setShowEnded(e.target.checked)} /> Include people who have left
        </label>
      </div>
      {list.isLoading ? (
        <Loading rows={3} />
      ) : list.error ? (
        <ErrorNote error={list.error} retry={() => void list.refetch()} />
      ) : rows.length === 0 ? (
        <div className="card flat">
          <Empty icon="people" title="No staff records yet">Add each person once. Their pay is set by the accountant.</Empty>
        </div>
      ) : (
        <div className="list">
          {rows.map((e) => (
            <button key={e.id} type="button" className="item" style={{ textAlign: 'left', cursor: 'pointer' }} onClick={() => setOpenId(e.id)}>
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">{e.fullName}</span>
                <span className="row">
                  {e.staffUserId ? <Badge tone="info">Has login</Badge> : null}
                  <Badge tone={e.status === 'ACTIVE' ? 'good' : ''}>{e.status === 'ACTIVE' ? 'Active' : 'Left'}</Badge>
                </span>
              </div>
              <span className="small muted">
                <span className="num">{e.employeeNo}</span> · {e.position} · {e.department}
                {e.basicSalary !== undefined ? ` · ${formatGhs(e.basicSalary)} a month` : ''}
              </span>
            </button>
          ))}
        </div>
      )}
      {openId ? <EmployeeDialog id={openId} onClose={() => setOpenId(null)} /> : null}
      {adding ? <EmployeeForm onClose={() => setAdding(false)} /> : null}
    </>
  );
}

function EmployeeDialog({ id, onClose }: { id: string; onClose: () => void }) {
  const { can } = useAuth();
  const emp = useEmployee(id);
  const bal = useLeaveBalance(id);
  const [editing, setEditing] = useState(false);
  const [ending, setEnding] = useState(false);
  const [revealed, setRevealed] = useState<Employee | null>(null);
  const report = useReport();
  const e = revealed ?? emp.data;

  if (editing && emp.data) return <EmployeeForm existing={emp.data} onClose={() => setEditing(false)} />;
  if (ending && e) return <EndDialog emp={e} onClose={() => setEnding(false)} onDone={onClose} />;

  return (
    <Modal open onClose={onClose} title={e?.fullName ?? 'Staff member'}>
      {emp.isLoading ? (
        <Loading rows={3} />
      ) : emp.error || !e ? (
        <ErrorNote error={emp.error} retry={() => void emp.refetch()} />
      ) : (
        <div className="stack">
          <div className="row">
            <Badge tone={e.status === 'ACTIVE' ? 'good' : ''}>{e.status === 'ACTIVE' ? 'Active' : 'Left'}</Badge>
            <span className="small muted num">{e.employeeNo}</span>
          </div>
          <dl className="kv">
            <dt>Job</dt>
            <dd>{e.position} · {e.department}</dd>
            <dt>Started</dt>
            <dd>{formatDate(e.hireDate, true)}</dd>
            {e.endDate ? (
              <>
                <dt>Left</dt>
                <dd>{formatDate(e.endDate, true)}</dd>
              </>
            ) : null}
            {e.phone ? (
              <>
                <dt>Phone</dt>
                <dd><a href={`tel:${e.phone}`}>{e.phone}</a></dd>
              </>
            ) : null}
            {e.emergencyName ? (
              <>
                <dt>Emergency contact</dt>
                <dd>{e.emergencyName}{e.emergencyPhone ? ` · ${e.emergencyPhone}` : ''}</dd>
              </>
            ) : null}
          </dl>

          {e.basicSalary !== undefined ? (
            <>
              <div className="eyebrow">Pay</div>
              <dl className="kv">
                <dt>Basic a month</dt>
                <dd className="num">{formatGhs(e.basicSalary)}</dd>
                {(e.allowances ?? []).map((a) => (
                  <span key={a.name} style={{ display: 'contents' }}>
                    <dt>{a.name}{a.taxable ? '' : ' (tax-free)'}</dt>
                    <dd className="num">{formatGhs(a.amount)}</dd>
                  </span>
                ))}
                <dt>Paid by</dt>
                <dd>{e.payMethod === 'BANK' ? `Bank${e.payBankName ? ` · ${e.payBankName}` : ''}` : e.payMethod === 'MOMO' ? 'Mobile money' : 'Cash'}</dd>
                <dt>Account or number</dt>
                <dd className="num">{e.payAccount ?? '–'}</dd>
                <dt>Tax number (TIN)</dt>
                <dd className="num">{e.tin ?? '–'}</dd>
                <dt>Pension number</dt>
                <dd className="num">{e.pensionNo ?? '–'}</dd>
              </dl>
              {!revealed && (e.payAccount || e.tin || e.pensionNo) ? (
                <div>
                  <Button
                    variant="ghost"
                    onClick={() =>
                      void api<Employee>(`/hr/employees/${id}?reveal=1`)
                        .then(setRevealed)
                        .catch(report)
                    }
                  >
                    Show full numbers (recorded in the audit log)
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}

          <div className="eyebrow">Leave this year</div>
          {bal.data ? (
            <ul className="checklist">
              {bal.data.balances.map((b) => (
                <li key={b.leaveType}>
                  <div className="row" style={{ justifyContent: 'space-between', minHeight: 40 }}>
                    <span>{b.leaveType}</span>
                    <span className="small muted num">{b.remaining === null ? `${b.taken} taken` : `${b.remaining} left of ${b.entitlement}`}{b.pending ? ` · ${b.pending} waiting` : ''}</span>
                  </div>
                </li>
              ))}
            </ul>
          ) : (
            <Loading rows={1} />
          )}

          {can('hr.manage') && e.status === 'ACTIVE' ? (
            <div className="row">
              <Button onClick={() => setEditing(true)}>Edit</Button>
              <Button variant="danger" onClick={() => setEnding(true)}>
                Record that they left
              </Button>
            </div>
          ) : null}
          <Button block onClick={onClose}>
            Close
          </Button>
        </div>
      )}
    </Modal>
  );
}

function EndDialog({ emp, onClose, onDone }: { emp: Employee; onClose: () => void; onDone: () => void }) {
  const { me } = useAuth();
  const toast = useToast();
  const report = useReport();
  const [date, setDate] = useState(me?.property.today ?? '');
  const [reason, setReason] = useState('');
  const end = useHrMutation((body: Record<string, unknown>) => api(`/hr/employees/${emp.id}/end`, { method: 'POST', body }));
  return (
    <Modal open onClose={onClose} title={`${emp.fullName} has left`}>
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (reason.trim().length >= 3) end.mutate({ endDate: date, reason: reason.trim() }, { onError: report, onSuccess: () => { toast.ok('Recorded'); onDone(); } });
        }}
      >
        <Field id="en-date" label="Last working day">
          <input id="en-date" className="input" type="date" value={date} onChange={(e) => setDate(e.target.value)} />
        </Field>
        <Field id="en-why" label="Reason" hint="The record is kept for the books but can no longer be edited">
          <input id="en-why" className="input" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus />
        </Field>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Back
          </Button>
          <Button variant="danger" type="submit" disabled={reason.trim().length < 3 || !date} loading={end.isPending}>
            Record
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function EmployeeForm({ existing, onClose }: { existing?: Employee; onClose: () => void }) {
  const { can } = useAuth();
  const pay = can('hr.pay_data');
  const toast = useToast();
  const report = useReport();
  const [f, setF] = useState({
    fullName: existing?.fullName ?? '',
    position: existing?.position ?? '',
    department: existing?.department ?? '',
    hireDate: existing?.hireDate ?? '',
    phone: existing?.phone ?? '',
    emergencyName: existing?.emergencyName ?? '',
    emergencyPhone: existing?.emergencyPhone ?? '',
    basic: existing?.basicSalary !== undefined ? (existing.basicSalary / 100).toFixed(2) : '',
    payMethod: (existing?.payMethod ?? 'BANK') as 'BANK' | 'MOMO' | 'CASH',
    payBankName: existing?.payBankName ?? '',
    payAccount: '',
    tin: '',
    pensionNo: '',
    ghanaCard: '',
  });
  const [exempt, setExempt] = useState(existing?.ssnitExempt ?? false);
  const [allowances, setAllowances] = useState<{ name: string; amount: string; taxable: boolean }[]>((existing?.allowances ?? []).map((a) => ({ name: a.name, amount: (a.amount / 100).toFixed(2), taxable: a.taxable })));
  const set = (k: keyof typeof f, v: string) => setF((x) => ({ ...x, [k]: v }));
  const save = useHrMutation((v: { body: Record<string, unknown> }) => (existing ? api(`/hr/employees/${existing.id}`, { method: 'PATCH', body: v.body }) : api('/hr/employees', { method: 'POST', body: v.body })));

  const basic = f.basic.trim() === '' ? null : parseCedis(f.basic);
  const parsedAllowances = allowances.map((a) => ({ name: a.name.trim(), amount: parseCedis(a.amount), taxable: a.taxable }));
  const allowancesOk = parsedAllowances.every((a) => a.name.length >= 2 && a.amount !== null);
  const valid = f.fullName.trim().length >= 2 && f.position.trim().length >= 2 && f.department.trim().length >= 2 && !!f.hireDate && (!pay || f.basic.trim() === '' || basic !== null) && allowancesOk;

  function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid) return;
    const body: Record<string, unknown> = { fullName: f.fullName.trim(), position: f.position.trim(), department: f.department.trim(), hireDate: f.hireDate };
    if (f.phone.trim()) body.phone = f.phone.trim();
    if (f.emergencyName.trim()) body.emergencyName = f.emergencyName.trim();
    if (f.emergencyPhone.trim()) body.emergencyPhone = f.emergencyPhone.trim();
    if (pay) {
      if (basic !== null) body.basicSalary = basic;
      body.payMethod = f.payMethod;
      if (f.payBankName.trim()) body.payBankName = f.payBankName.trim();
      if (f.payAccount.trim()) body.payAccount = f.payAccount.trim();
      if (f.tin.trim()) body.tin = f.tin.trim();
      if (f.pensionNo.trim()) body.pensionNo = f.pensionNo.trim();
      if (f.ghanaCard.trim()) body.ghanaCard = f.ghanaCard.trim();
      body.ssnitExempt = exempt;
      body.allowances = parsedAllowances.map((a) => ({ name: a.name, amount: a.amount as number, taxable: a.taxable }));
    }
    save.mutate({ body }, { onError: report, onSuccess: () => { toast.ok(existing ? 'Saved' : 'Person added'); onClose(); } });
  }

  return (
    <Modal open onClose={onClose} title={existing ? `Edit ${existing.fullName}` : 'Add a person'}>
      <form className="stack" onSubmit={submit}>
        <Field id="ef-name" label="Full name">
          <input id="ef-name" className="input" value={f.fullName} onChange={(e) => set('fullName', e.target.value)} autoFocus />
        </Field>
        <div className="grid-2">
          <Field id="ef-pos" label="Job title">
            <input id="ef-pos" className="input" value={f.position} onChange={(e) => set('position', e.target.value)} placeholder="e.g. Receptionist" />
          </Field>
          <Field id="ef-dep" label="Department">
            <input id="ef-dep" className="input" value={f.department} onChange={(e) => set('department', e.target.value)} placeholder="e.g. Front desk" />
          </Field>
          <Field id="ef-hire" label="Started on">
            <input id="ef-hire" className="input" type="date" value={f.hireDate} onChange={(e) => set('hireDate', e.target.value)} />
          </Field>
          <Field id="ef-phone" label="Phone">
            <input id="ef-phone" className="input" inputMode="tel" value={f.phone} onChange={(e) => set('phone', e.target.value)} />
          </Field>
          <Field id="ef-en" label="Emergency contact">
            <input id="ef-en" className="input" value={f.emergencyName} onChange={(e) => set('emergencyName', e.target.value)} />
          </Field>
          <Field id="ef-ep" label="Their phone">
            <input id="ef-ep" className="input" inputMode="tel" value={f.emergencyPhone} onChange={(e) => set('emergencyPhone', e.target.value)} />
          </Field>
        </div>

        {pay ? (
          <>
            <div className="eyebrow">Pay (only you and the owner see this)</div>
            <Field id="ef-basic" label="Basic pay a month (GH₵)" error={f.basic.trim() && basic === null ? 'Enter an amount like 1500 or 1500.00' : undefined}>
              <input id="ef-basic" className="input" inputMode="decimal" value={f.basic} onChange={(e) => set('basic', e.target.value)} />
            </Field>
            <AllowanceRows rows={allowances} onChange={setAllowances} />
            <div className="seg" role="group" aria-label="Paid by">
              {(['BANK', 'MOMO', 'CASH'] as const).map((m) => (
                <button key={m} type="button" aria-pressed={f.payMethod === m} onClick={() => setF((x) => ({ ...x, payMethod: m }))}>
                  {m === 'BANK' ? 'Bank' : m === 'MOMO' ? 'Mobile money' : 'Cash'}
                </button>
              ))}
            </div>
            {f.payMethod === 'BANK' ? (
              <Field id="ef-bank" label="Bank">
                <input id="ef-bank" className="input" value={f.payBankName} onChange={(e) => set('payBankName', e.target.value)} />
              </Field>
            ) : null}
            {f.payMethod !== 'CASH' ? (
              <Field id="ef-acct" label={f.payMethod === 'MOMO' ? 'MoMo number' : 'Account number'} hint={existing ? 'Leave empty to keep the one on file' : undefined}>
                <input id="ef-acct" className="input" inputMode="numeric" value={f.payAccount} onChange={(e) => set('payAccount', e.target.value)} autoComplete="off" />
              </Field>
            ) : null}
            <div className="grid-2">
              <Field id="ef-tin" label="Tax number (TIN)" hint={existing ? 'Empty keeps the one on file' : undefined}>
                <input id="ef-tin" className="input" value={f.tin} onChange={(e) => set('tin', e.target.value)} autoComplete="off" />
              </Field>
              <Field id="ef-pen" label="SSNIT number" hint={existing ? 'Empty keeps the one on file' : undefined}>
                <input id="ef-pen" className="input" value={f.pensionNo} onChange={(e) => set('pensionNo', e.target.value)} autoComplete="off" />
              </Field>
            </div>
            <Field id="ef-gh" label="Ghana Card number" hint={existing ? 'SSNIT asks for this on the monthly report. Empty keeps the one on file' : 'SSNIT asks for this on the monthly report'}>
              <input id="ef-gh" className="input" value={f.ghanaCard} onChange={(e) => set('ghanaCard', e.target.value)} autoComplete="off" />
            </Field>
            <label className="row small">
              <input type="checkbox" checked={exempt} onChange={(e) => setExempt(e.target.checked)} /> Does not contribute to SSNIT (for example a casual worker)
            </label>
          </>
        ) : (
          <p className="hint">Pay details are added by the accountant.</p>
        )}

        <div className="modal-foot">
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={save.isPending}>
            {existing ? 'Save' : 'Add person'}
          </Button>
        </div>
      </form>
    </Modal>
  );
}

function AllowanceRows({ rows, onChange }: { rows: { name: string; amount: string; taxable: boolean }[]; onChange: (r: { name: string; amount: string; taxable: boolean }[]) => void }) {
  const set = (i: number, p: Partial<{ name: string; amount: string; taxable: boolean }>) => onChange(rows.map((r, j) => (j === i ? { ...r, ...p } : r)));
  return (
    <div className="stack">
      <div className="eyebrow">Allowances (optional)</div>
      {rows.map((r, i) => (
        <div key={i} className="grid-2" style={{ alignItems: 'end' }}>
          <input className="input" aria-label="Allowance name" placeholder="e.g. Transport" value={r.name} onChange={(e) => set(i, { name: e.target.value })} />
          <div className="row">
            <input className="input" aria-label="Allowance amount" inputMode="decimal" placeholder="0.00" value={r.amount} onChange={(e) => set(i, { amount: e.target.value })} />
            <Button type="button" variant="ghost" aria-label="Remove allowance" onClick={() => onChange(rows.filter((_, j) => j !== i))}>
              ✕
            </Button>
          </div>
          <label className="row small" style={{ gridColumn: '1 / -1' }}>
            <input type="checkbox" checked={r.taxable} onChange={(e) => set(i, { taxable: e.target.checked })} /> Counts as taxable income
          </label>
        </div>
      ))}
      {rows.length < 12 ? (
        <div>
          <Button type="button" onClick={() => onChange([...rows, { name: '', amount: '', taxable: true }])}>
            Add an allowance
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------- leave */
const LEAVE_TONE: Record<string, Tone> = { PENDING: 'warn', APPROVED: 'good', REJECTED: 'bad', CANCELLED: '' };

function LeaveTab() {
  const { can } = useAuth();
  const [status, setStatus] = useState('');
  const list = useLeaveRequests(status);
  const toast = useToast();
  const report = useReport();
  const [booking, setBooking] = useState(false);
  const [typing, setTyping] = useState(false);
  const act = useHrMutation((v: { id: string; path: 'approve' | 'reject' | 'cancel' }) => api(`/hr/leave-requests/${v.id}/${v.path}`, { method: 'POST', body: {} }));

  return (
    <>
      <div className="row">
        {can('hr.manage') ? (
          <>
            <Button variant="primary" onClick={() => setBooking(true)}>
              Book leave
            </Button>
            <Button onClick={() => setTyping(true)}>Leave types</Button>
          </>
        ) : null}
        <div className="seg" role="group" aria-label="Show">
          {[['', 'Waiting and approved'], ['PENDING', 'Waiting'], ['REJECTED', 'Refused'], ['CANCELLED', 'Cancelled']].map(([v, l]) => (
            <button key={v} type="button" aria-pressed={status === v} onClick={() => setStatus(v!)}>
              {l}
            </button>
          ))}
        </div>
      </div>
      {list.isLoading ? (
        <Loading rows={2} />
      ) : list.error ? (
        <ErrorNote error={list.error} retry={() => void list.refetch()} />
      ) : (list.data ?? []).length === 0 ? (
        <div className="card flat">
          <Empty icon="inbox" title="No leave to show" />
        </div>
      ) : (
        <div className="list">
          {list.data!.map((r) => (
            <div key={r.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">{r.employeeName}</span>
                <Badge tone={LEAVE_TONE[r.status] ?? ''}>{r.status === 'PENDING' ? 'Waiting' : r.status === 'APPROVED' ? 'Approved' : r.status === 'REJECTED' ? 'Refused' : 'Cancelled'}</Badge>
              </div>
              <span className="small muted">
                {r.leaveType}{r.paid ? '' : ' (unpaid)'} · {formatDate(r.from)} to {formatDate(r.to)} · {r.days} working day{r.days === 1 ? '' : 's'}
                {r.reason ? ` · ${r.reason}` : ''}
              </span>
              <div className="row">
                {r.status === 'PENDING' && can('leave.approve') ? (
                  <>
                    <Button variant="primary" loading={act.isPending} onClick={() => act.mutate({ id: r.id, path: 'approve' }, { onError: report, onSuccess: () => toast.ok('Approved') })}>
                      Approve
                    </Button>
                    <Button loading={act.isPending} onClick={() => act.mutate({ id: r.id, path: 'reject' }, { onError: report, onSuccess: () => toast.ok('Refused') })}>
                      Refuse
                    </Button>
                  </>
                ) : null}
                {(r.status === 'PENDING' || r.status === 'APPROVED') && can('hr.manage') ? (
                  <Button variant="ghost" loading={act.isPending} onClick={() => act.mutate({ id: r.id, path: 'cancel' }, { onError: report, onSuccess: () => toast.ok('Cancelled') })}>
                    Cancel leave
                  </Button>
                ) : null}
              </div>
            </div>
          ))}
        </div>
      )}
      {booking ? <BookLeaveDialog onClose={() => setBooking(false)} /> : null}
      {typing ? <LeaveTypesDialog onClose={() => setTyping(false)} /> : null}
    </>
  );
}

function BookLeaveDialog({ onClose }: { onClose: () => void }) {
  const emps = useEmployees('ACTIVE');
  const types = useLeaveTypes();
  const toast = useToast();
  const report = useReport();
  const [employeeId, setEmployeeId] = useState('');
  const [typeId, setTypeId] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [reason, setReason] = useState('');
  const make = useHrMutation((body: Record<string, unknown>) => api('/hr/leave-requests', { method: 'POST', body }));
  useEffect(() => { if (!employeeId && emps.data?.[0]) setEmployeeId(emps.data[0].id); }, [emps.data, employeeId]);
  useEffect(() => { if (!typeId && types.data?.[0]) setTypeId(types.data[0].id); }, [types.data, typeId]);
  const valid = !!employeeId && !!typeId && !!from && !!to && from <= to;
  return (
    <Modal open onClose={onClose} title="Book leave">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (valid) make.mutate({ employeeId, leaveTypeId: typeId, from, to, ...(reason.trim() ? { reason: reason.trim() } : {}) }, { onError: report, onSuccess: () => { toast.ok('Leave booked, waiting for approval'); onClose(); } });
        }}
      >
        <Field id="bl-emp" label="Who">
          <select id="bl-emp" className="select" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            {(emps.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.fullName}</option>)}
          </select>
        </Field>
        <Field id="bl-type" label="Kind of leave">
          <select id="bl-type" className="select" value={typeId} onChange={(e) => setTypeId(e.target.value)}>
            {(types.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}{t.paid ? '' : ' (unpaid)'}</option>)}
          </select>
        </Field>
        <div className="grid-2">
          <Field id="bl-from" label="First day"><input id="bl-from" className="input" type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
          <Field id="bl-to" label="Last day" hint="Weekends are not counted"><input id="bl-to" className="input" type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} /></Field>
        </div>
        <Field id="bl-why" label="Reason (optional)"><input id="bl-why" className="input" value={reason} onChange={(e) => setReason(e.target.value)} /></Field>
        {types.data && types.data.length === 0 ? <div className="banner bad">Add a leave type first (Leave types).</div> : null}
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={make.isPending}>Book leave</Button>
        </div>
      </form>
    </Modal>
  );
}

function LeaveTypesDialog({ onClose }: { onClose: () => void }) {
  const types = useLeaveTypes();
  const toast = useToast();
  const report = useReport();
  const [name, setName] = useState('');
  const [days, setDays] = useState('15');
  const [paid, setPaid] = useState(true);
  const add = useHrMutation((body: Record<string, unknown>) => api('/hr/leave-types', { method: 'POST', body }));
  return (
    <Modal open onClose={onClose} title="Leave types">
      <div className="stack">
        <ul className="checklist">
          {(types.data ?? []).map((t) => (
            <li key={t.id}>
              <div className="row" style={{ justifyContent: 'space-between', minHeight: 44 }}>
                <span>{t.name}{t.paid ? '' : ' (unpaid)'}</span>
                <span className="small muted">{t.daysPerYear > 0 ? `${t.daysPerYear} days a year` : 'no limit'}</span>
              </div>
            </li>
          ))}
        </ul>
        <form
          className="stack"
          onSubmit={(e: FormEvent) => {
            e.preventDefault();
            add.mutate({ name: name.trim(), daysPerYear: Number(days) || 0, paid }, { onError: report, onSuccess: () => { toast.ok('Leave type added'); setName(''); } });
          }}
        >
          <div className="grid-2">
            <Field id="lt-name" label="New leave type"><input id="lt-name" className="input" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Annual, Sick, Unpaid" /></Field>
            <Field id="lt-days" label="Days a year" hint="0 means no limit"><input id="lt-days" className="input" inputMode="numeric" value={days} onChange={(e) => setDays(e.target.value.replace(/\D/g, ''))} /></Field>
          </div>
          <label className="row small"><input type="checkbox" checked={paid} onChange={(e) => setPaid(e.target.checked)} /> Paid leave (unpaid leave is taken off pay)</label>
          <Button type="submit" disabled={name.trim().length < 2} loading={add.isPending}>Add leave type</Button>
        </form>
        <Button block onClick={onClose}>Done</Button>
      </div>
    </Modal>
  );
}

/* ---------------------------------------------------------------- advances */
function AdvancesTab() {
  const { can } = useAuth();
  const loans = useLoans(true);
  const [giving, setGiving] = useState(false);
  return (
    <>
      {can('payroll.approve') ? (
        <div>
          <Button variant="primary" onClick={() => setGiving(true)}>
            Give an advance
          </Button>
        </div>
      ) : null}
      {loans.isLoading ? (
        <Loading rows={2} />
      ) : loans.error ? (
        <ErrorNote error={loans.error} retry={() => void loans.refetch()} />
      ) : (loans.data ?? []).length === 0 ? (
        <div className="card flat">
          <Empty icon="inbox" title="No advances or loans">An advance is repaid a little each month through payroll.</Empty>
        </div>
      ) : (
        <div className="list">
          {loans.data!.map((l) => (
            <div key={l.id} className="item">
              <div className="row" style={{ justifyContent: 'space-between' }}>
                <span className="title">{l.employeeName}</span>
                <Badge tone={l.status === 'ACTIVE' ? 'warn' : 'good'}>{l.status === 'ACTIVE' ? 'Repaying' : 'Cleared'}</Badge>
              </div>
              <span className="small muted">
                {formatGhs(l.principal)} given {formatDate(l.issuedOn, true)} · {formatGhs(l.monthlyRepayment)} a month · <strong className="num">{formatGhs(l.balance)}</strong> still owed
              </span>
            </div>
          ))}
        </div>
      )}
      {giving ? <LoanDialog onClose={() => setGiving(false)} /> : null}
    </>
  );
}

function LoanDialog({ onClose }: { onClose: () => void }) {
  const { me } = useAuth();
  const emps = useEmployees('ACTIVE');
  const toast = useToast();
  const report = useReport();
  const [employeeId, setEmployeeId] = useState('');
  const [amount, setAmount] = useState('');
  const [monthly, setMonthly] = useState('');
  const [from, setFrom] = useState<'CASH' | 'BANK' | 'MOMO'>('CASH');
  const key = useState(() => newKey())[0]; // one key per dialog: a second tap cannot lend twice
  const make = useHrMutation((body: Record<string, unknown>) => api('/hr/loans', { method: 'POST', body, idempotencyKey: key }));
  useEffect(() => { if (!employeeId && emps.data?.[0]) setEmployeeId(emps.data[0].id); }, [emps.data, employeeId]);
  const p = parseCedis(amount);
  const m = parseCedis(monthly);
  const valid = !!employeeId && p !== null && p > 0 && m !== null && m > 0 && m <= p;
  return (
    <Modal open onClose={onClose} title="Give an advance">
      <form
        className="stack"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          if (valid) make.mutate({ employeeId, principal: p, monthlyRepayment: m, issuedOn: me?.property.today, payFrom: from }, { onError: report, onSuccess: () => { toast.ok('Advance recorded'); onClose(); } });
        }}
      >
        <Field id="ln-emp" label="Who">
          <select id="ln-emp" className="select" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            {(emps.data ?? []).map((e) => <option key={e.id} value={e.id}>{e.fullName}</option>)}
          </select>
        </Field>
        <div className="grid-2">
          <Field id="ln-amt" label="Amount (GH₵)"><input id="ln-amt" className="input" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} /></Field>
          <Field id="ln-mon" label="Repaid a month (GH₵)" error={m !== null && p !== null && m > p ? 'More than the advance' : undefined}><input id="ln-mon" className="input" inputMode="decimal" value={monthly} onChange={(e) => setMonthly(e.target.value)} /></Field>
        </div>
        <div className="seg" role="group" aria-label="Paid out from">
          {(['CASH', 'BANK', 'MOMO'] as const).map((x) => (
            <button key={x} type="button" aria-pressed={from === x} onClick={() => setFrom(x)}>{x === 'CASH' ? 'From cash' : x === 'BANK' ? 'From the bank' : 'From mobile money'}</button>
          ))}
        </div>
        <p className="hint">This is money leaving the hotel, so it is recorded in the books straight away. It is taken off pay a little each month.</p>
        <div className="modal-foot">
          <Button type="button" onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" disabled={!valid} loading={make.isPending}>Give advance</Button>
        </div>
      </form>
    </Modal>
  );
}

